import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Button from './Button';
import { useTheme } from '../context/ThemeContext';
import { useToast } from './Toast';
import { api } from '../api/client';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';
import type { ColorTheme } from '../utils/theme';
import { useModalBottomPadding } from '../utils/useModalBottomPadding';
import { openExternal } from '../utils/openExternal';

export interface WashCompleteData {
  washType: 'automatic' | 'self-serve' | 'bundle' | 'subscription';
  washId: string;
  tierName?: string;
  locationName?: string;
  bayName?: string;
  amount?: number;
  fleetName?: string;
  planName?: string;
  washesRemaining?: number;
  washesTotal?: number;
  washesUsed?: number;
  washesUsedToday?: number;
  maxWashesPerDay?: number;
  washesUsedThisMonth?: number;
  maxWashesPerMonth?: number;
  status?: string;
  durationMinutes?: number;
  /** Wash-credit drawn on this wash (cents) — shown so customers see the gift being spent. */
  creditUsedCents?: number;
  /** Promo/voucher value applied (cents) — free minutes, % off, or free wash. */
  promoUsedCents?: number;
  /** Loyalty points earned — backend sends 0 when the program is disabled, so >0 implies enabled. */
  pointsEarned?: number;
  /** 1.4 P20: receipt line items (tier + upsells) with what funded each. */
  lineItems?: { name: string; amountCents: number; included?: boolean; kind?: string }[];
  /** 1.4 P20: the wash is waiting for the bay to free up (started later or auto-refunded). */
  queued?: boolean;
  /** Free vacuum minutes granted by this wash purchase (tier perk) — drives the
   *  prominent "scan a vacuum" callout. null/undefined = no grant. */
  grantedVacMinutes?: number | null;
}

interface Props {
  data: WashCompleteData;
  onDone: () => void;
  onReportProblem: () => void;
}

export default function WashCompleteOverlay({ data, onDone, onReportProblem }: Props) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();
  const modalBottomPadding = useModalBottomPadding();
  const [selectedRating, setSelectedRating] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Non-null only when the backend decides this customer is eligible for the
  // gentle Google-review nudge (5 stars, link configured, not prompted recently).
  const [googleReview, setGoogleReview] = useState<{ url: string; locationId: string } | null>(null);

  const handleRate = async (rating: number) => {
    if (submitting || rating === selectedRating) return;
    setSelectedRating(rating);
    setSubmitting(true);
    try {
      const res = await api<{ googleReview?: { url: string; locationId: string } | null }>('/api/ratings', {
        method: 'POST',
        body: { washType: data.washType, washId: data.washId, rating },
      });
      setSubmitted(true);
      // Re-rating below 5 stars returns null — the nudge disappears with it.
      setGoogleReview(res.googleReview ?? null);
    } catch {
      toast.show({ type: 'error', title: 'Error', message: 'Could not submit rating' });
      setSelectedRating(submitted ? selectedRating : 0);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleReview = () => {
    if (!googleReview) return;
    // Fire-and-forget: eligibility bookkeeping must never block opening the link.
    api('/api/review-prompts/clicked', {
      method: 'POST',
      body: { locationId: googleReview.locationId },
    }).catch(() => {});
    openExternal(googleReview.url);
  };

  const isProcessing = data.status && data.status !== 'completed';
  const icon = isProcessing ? 'time' : 'checkmark-circle';
  const iconColor = isProcessing ? colors.primary : colors.success;
  const title = data.washType === 'self-serve' ? 'Wash Complete!' : (isProcessing ? 'Wash Processing...' : 'Wash Started!');

  return (
    <View style={styles.overlay}>
      <ScrollView contentContainerStyle={styles.scrollContent} bounces={false} showsVerticalScrollIndicator={false}>
      <View style={[styles.content, { paddingBottom: spacing.xl + modalBottomPadding }]}>
        <View style={styles.iconRing}>
          <Ionicons name={icon} size={80} color={iconColor} />
        </View>

        <Text style={[styles.title, isProcessing && { color: colors.primary }]}>{title}</Text>

        {data.tierName && (
          <Text style={styles.subtitle}>{data.tierName} wash{data.washType === 'self-serve' ? '' : ' is now running'}</Text>
        )}

        {/* Free vacuum minutes granted with this wash — prominent accent card,
            not a badge, so customers actually go use their perk. */}
        {data.grantedVacMinutes != null && data.grantedVacMinutes > 0 && (
          <View style={styles.vacGrantCard}>
            <Ionicons name="sparkles" size={24} color={colors.primary} />
            <Text style={styles.vacGrantText}>
              You've got {data.grantedVacMinutes} free vacuum minutes! Just scan the QR code on any vacuum here to start them.
            </Text>
          </View>
        )}

        {/* 1.4 P20: line items (tier + add-ons) */}
        {!!data.lineItems?.length && (
          <View style={styles.lineItems}>
            {data.lineItems.map((li, i) => (
              <View key={i} style={styles.lineItemRow}>
                <Text style={styles.lineItemName}>{li.name}</Text>
                <Text style={styles.lineItemAmt}>{li.included ? 'Included' : li.amountCents > 0 ? `$${(li.amountCents / 100).toFixed(2)}` : li.kind === 'tier' ? '' : 'Free'}</Text>
              </View>
            ))}
          </View>
        )}

        {/* Amount badge for paid washes */}
        {data.amount != null && data.amount > 0 && (
          <View style={styles.amountBadge}>
            <Ionicons name={data.fleetName ? 'business' : 'card'} size={16} color={colors.primary} />
            <Text style={styles.amountText}>
              ${(data.amount / 100).toFixed(2)}
              {data.fleetName ? ` — ${data.fleetName}` : ''}
            </Text>
          </View>
        )}

        {/* Promo value applied on this wash */}
        {data.promoUsedCents != null && data.promoUsedCents > 0 && (
          <View style={styles.amountBadge}>
            <Ionicons name="pricetag" size={16} color={colors.primary} />
            <Text style={styles.amountText}>${(data.promoUsedCents / 100).toFixed(2)} promo applied</Text>
          </View>
        )}

        {/* Wash credit drawn on this wash */}
        {data.creditUsedCents != null && data.creditUsedCents > 0 && (
          <View style={styles.amountBadge}>
            <Ionicons name="gift" size={16} color={colors.primary} />
            <Text style={styles.amountText}>
              ${(data.creditUsedCents / 100).toFixed(2)} credit applied
              {data.amount != null && data.amount > 0 ? '' : ' — free wash!'}
            </Text>
          </View>
        )}

        {/* Loyalty points earned (only sent when the owner's program is enabled) */}
        {data.pointsEarned != null && data.pointsEarned > 0 && (
          <View style={styles.amountBadge}>
            <Ionicons name="sparkles" size={16} color={colors.primary} />
            <Text style={styles.amountText}>+{data.pointsEarned} points earned</Text>
          </View>
        )}

        {/* Duration for self-serve */}
        {data.durationMinutes != null && data.durationMinutes > 0 && (
          <View style={styles.amountBadge}>
            <Ionicons name="time-outline" size={16} color={colors.primary} />
            <Text style={styles.amountText}>{data.durationMinutes.toFixed(1)} min</Text>
          </View>
        )}

        {/* Plan info for subscription */}
        {data.washType === 'subscription' && data.planName && (
          <View style={styles.amountBadge}>
            <Ionicons name="ribbon" size={16} color={colors.primary} />
            <Text style={styles.amountText}>{data.planName}</Text>
          </View>
        )}

        {/* Bundle stats */}
        {data.washType === 'bundle' && data.washesRemaining != null && (
          <View style={styles.statRow}>
            <View style={styles.stat}>
              <Text style={styles.statValue}>{data.washesUsed ?? 0}</Text>
              <Text style={styles.statLabel}>Used</Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.stat}>
              <Text style={styles.statValue}>{data.washesRemaining}</Text>
              <Text style={styles.statLabel}>Remaining</Text>
            </View>
          </View>
        )}

        {/* Subscription daily/monthly stats */}
        {data.washType === 'subscription' && data.maxWashesPerDay != null && (
          <View style={styles.statRow}>
            <View style={styles.stat}>
              <Text style={styles.statValue}>{data.washesUsedToday ?? 0}/{data.maxWashesPerDay === 0 ? '∞' : data.maxWashesPerDay}</Text>
              <Text style={styles.statLabel}>Today</Text>
            </View>
            {data.maxWashesPerMonth != null && (
              <>
                <View style={styles.statDivider} />
                <View style={styles.stat}>
                  <Text style={styles.statValue}>{data.washesUsedThisMonth ?? 0}/{data.maxWashesPerMonth === 0 ? '∞' : data.maxWashesPerMonth}</Text>
                  <Text style={styles.statLabel}>This Month</Text>
                </View>
              </>
            )}
          </View>
        )}

        {/* Location info */}
        {data.locationName && (
          <View style={styles.locationBox}>
            <Ionicons name="location" size={18} color={colors.textSecondary} style={{ marginTop: 1 }} />
            <View style={{ flex: 1 }}>
              <Text style={styles.locationName}>{data.locationName}</Text>
              {data.bayName && <Text style={styles.locationDetail}>{data.bayName}</Text>}
            </View>
          </View>
        )}

        {/* Star rating */}
        <View style={styles.ratingSection}>
          <Text style={styles.ratingLabel}>{submitted ? 'Thanks for your feedback!' : 'Rate your experience'}</Text>
          <View style={styles.starRow}>
            {[1, 2, 3, 4, 5].map(star => (
              <TouchableOpacity
                key={star}
                onPress={() => handleRate(star)}
                hitSlop={8}
                accessibilityRole="button"
                accessibilityState={{ selected: star <= selectedRating }}
                accessibilityLabel={`Rate ${star} star${star > 1 ? 's' : ''}`}
              >
                <Ionicons
                  name={star <= selectedRating ? 'star' : 'star-outline'}
                  size={36}
                  color={star <= selectedRating ? colors.warning : colors.textLight}
                />
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* Gentle Google-review nudge — only after a server-eligible 5-star rating.
            One soft ask, one button, easily ignorable; Done still dismisses as usual. */}
        {submitted && googleReview && (
          <View style={styles.reviewSection}>
            <Text style={styles.reviewText}>
              Glad you loved it! Reviews on Google make a big difference for small businesses like this one — if you have a moment, we'd really appreciate one.
            </Text>
            <Button
              title="Leave a Google review"
              onPress={handleGoogleReview}
              variant="outline"
              size="sm"
              icon={<Ionicons name="logo-google" size={18} color={colors.primary} />}
              style={{ marginTop: spacing.sm, width: '100%' }}
            />
          </View>
        )}

        <Button
          title="Report a Problem"
          onPress={onReportProblem}
          variant="ghost"
          size="sm"
          icon={<Ionicons name="flag-outline" size={16} color={colors.danger} />}
          style={{ width: '100%', marginTop: spacing.lg }}
        />
        <Button
          title="Done"
          onPress={onDone}
          size="lg"
          style={{ width: '100%', marginTop: spacing.sm }}
        />
      </View>
      </ScrollView>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  lineItems: { width: '100%', marginTop: 8, marginBottom: 4, paddingHorizontal: 12 },
  lineItemRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 3 },
  lineItemName: { fontSize: 14, color: c.text },
  lineItemAmt: { fontSize: 14, color: c.textSecondary, fontWeight: '600' },
  overlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: c.surface,
    zIndex: 100,
  },
  scrollContent: {
    flexGrow: 1, justifyContent: 'center', alignItems: 'center',
  },
  content: {
    alignItems: 'center', padding: spacing.xl, width: '100%', maxWidth: 400,
  },
  iconRing: {
    width: 120, height: 120, borderRadius: 60,
    backgroundColor: c.background,
    justifyContent: 'center', alignItems: 'center',
    marginBottom: spacing.lg,
  },
  title: {
    fontSize: fontSize.xxl, fontWeight: fontWeight.bold, color: c.text,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: fontSize.md, color: c.textSecondary,
    marginTop: spacing.xs, textAlign: 'center',
  },
  vacGrantCard: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    width: '100%',
    backgroundColor: c.primary + '10',
    borderWidth: 1, borderColor: c.primary + '40',
    borderRadius: borderRadius.md,
    padding: spacing.md, marginTop: spacing.lg,
  },
  vacGrantText: {
    flex: 1, fontSize: fontSize.sm, fontWeight: fontWeight.medium,
    color: c.text, lineHeight: 20,
  },
  amountBadge: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
    backgroundColor: c.primary + '10',
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    borderRadius: borderRadius.full, marginTop: spacing.md,
  },
  amountText: {
    fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.primary,
  },
  statRow: {
    flexDirection: 'row', alignItems: 'center',
    marginTop: spacing.md, gap: spacing.md,
  },
  stat: { alignItems: 'center' },
  statValue: {
    fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text,
  },
  statLabel: {
    fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2,
  },
  statDivider: {
    width: 1, height: 30, backgroundColor: c.borderLight,
  },
  locationBox: {
    flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm,
    backgroundColor: c.background,
    padding: spacing.md, paddingVertical: spacing.md + 2,
    borderRadius: borderRadius.md,
    marginTop: spacing.lg, width: '100%',
  },
  locationName: {
    fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text,
    flexShrink: 1, flexWrap: 'wrap',
  },
  locationDetail: {
    fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2,
    flexShrink: 1, flexWrap: 'wrap',
  },
  ratingSection: {
    alignItems: 'center', marginTop: spacing.xl,
  },
  ratingLabel: {
    fontSize: fontSize.sm, color: c.textSecondary, marginBottom: spacing.sm,
  },
  starRow: {
    flexDirection: 'row', gap: spacing.sm,
  },
  reviewSection: {
    width: '100%', alignItems: 'center',
    backgroundColor: c.primary + '08',
    borderRadius: borderRadius.md,
    padding: spacing.md, marginTop: spacing.md,
  },
  reviewText: {
    fontSize: fontSize.sm, color: c.textSecondary,
    textAlign: 'center', lineHeight: 20,
  },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
